import { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import TertiaryButton from '@components/buttons/TertiaryButton';
import ArchiveSegment from '@components/carePlan/ArchiveSegment';
import styles from '@components/carePlan/ExpertCarePlan.module.css';
import ExpertService from '@components/carePlan/ExpertService';
import GoalEditorTextField from '@components/carePlan/GoalEditorTextField';
import { Section } from '@components/carePlan/GoalsManager';
import TaskForm from '@components/forms/TaskForm';
import Modal, { ModalElement } from '@components/Modal';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import { GetJourneyWithPlanQuery, GoalItemTypeEnum, GoalStatusEnum } from '@typing/Generated';
import { sortByPosition } from '@utils/modelUtils';

type Journey = GetJourneyWithPlanQuery['journey'];
type Goal = Journey['goals'][0];

type Task = Goal['tasks'][0];
type Props = {
  currentSection: Section;
  goal: Goal;
  journey: Journey;
};

const GoalsManagerExpertTask = ({ currentSection, goal, journey }: Props) => {
  const intl = useIntl();
  const [taskBeingEdited, setTaskBeingEdited] = useState<Task | undefined>(undefined);

  const taskModalRef = useRef<ModalElement>(null);
  const taskBrowserOpener = useOpener();
  const taskEditOpener = useOpener();
  const taskCreateOpener = useOpener();
  const [archived, setArchived] = useState(false);

  const onTaskEdit = (task: Task) => {
    setTaskBeingEdited(task);
    taskEditOpener.open();
  };

  const tasks = useMemo(() => sortByPosition(goal.tasks.filter(t => t.archived === archived)), [archived, goal.tasks]);

  return (
    <div className={styles.goalEditorSection}>
      {currentSection === Section.ALL && goal.status !== GoalStatusEnum.DRAFT && (
        <Heading2 color="primary300">
          <FormattedMessage id={`models.goal.sections.${Section.CARE_PARTNER_TASKS}`} />
        </Heading2>
      )}

      <div className={styles.spaceBetween}>
        <div>
          <TertiaryButton
            icon={taskCreateOpener.isOpen ? 'chevronUp' : 'chevronDown'}
            iconPosition="end"
            onClick={taskCreateOpener.toggle}
            textKey="pages.carePlan.addTask"
          />
        </div>
        <ArchiveSegment count={goal.tasks.length - tasks.length} setValue={setArchived} skipSpacing value={archived} />
      </div>
      <ExpandableSection initial={false} isOpen={taskCreateOpener.isOpen}>
        <GoalEditorTextField
          goal={goal}
          itemBrowserOpener={taskBrowserOpener}
          journey={journey}
          onCancel={taskCreateOpener.close}
          onSuccess={() => {}}
          templatePlaceholderKey="pages.carePlan.base.expertServices.searchPlaceholder"
          type={GoalItemTypeEnum.TASK}
        />
      </ExpandableSection>
      <div className={styles.items}>
        {tasks.map(task => (
          <ExpertService
            handleEdit={onTaskEdit}
            hover={false}
            journey={journey}
            key={task.id}
            memberView={false}
            task={task}
          />
        ))}
      </div>

      <Modal
        opener={taskEditOpener}
        ref={taskModalRef}
        title={intl.formatMessage({
          id: taskBeingEdited ? 'models.tasks.editTask' : 'models.tasks.addTask'
        })}
      >
        {({ modalDisplayed }) => (
          <TaskForm
            autofocus={modalDisplayed}
            clientVisibleTaskForm
            goal={goal}
            journey={journey}
            onCancel={taskEditOpener.close}
            onSuccess={taskEditOpener.close}
            primaryExpertId={journey.primaryExpert?.id}
            task={taskBeingEdited}
          />
        )}
      </Modal>
    </div>
  );
};

export default GoalsManagerExpertTask;
